import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";
import { InfiniteScrollIndicator } from "@/components/InfiniteScrollIndicator";
import { Popover } from "@/components/Popover";
import { Skeleton } from "@/components/Skeleton";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import type { CellContext, Column, DataTableProps, SortConfig, TableLayout, TableWidth } from "./DataTable.types";
import {
  AutoMobileRenderer,
  CardMobileRenderer,
  getMobileBreakpointClasses,
  MobileLoadingSkeleton,
} from "./MobileRenderer";
import { SortableHeaderRow } from "./SortableHeader";

const densityClasses = {
  compact: "py-1.5 px-3 text-sm",
  default: "py-3 px-4 text-sm",
  relaxed: "py-4 px-4 text-base",
};

const alignmentClasses = {
  left: "text-left justify-start",
  center: "text-center justify-center",
  right: "text-right justify-end",
};

const widthClasses: Record<TableWidth, string> = {
  full: "w-full",
  auto: "w-auto inline-block",
  contained: "w-full max-w-6xl mx-auto",
};

const layoutClasses: Record<TableLayout, string> = {
  auto: "table-auto",
  fixed: "table-fixed",
  responsive: "table-auto",
};

function getColumnStyles<T>(column: Column<T>) {
  const styles: React.CSSProperties = {};

  if (column.width !== undefined) {
    styles.width = typeof column.width === "number" ? `${column.width}px` : column.width;
  }
  if (column.minWidth !== undefined) {
    styles.minWidth = typeof column.minWidth === "number" ? `${column.minWidth}px` : column.minWidth;
  }
  if (column.maxWidth !== undefined) {
    styles.maxWidth = typeof column.maxWidth === "number" ? `${column.maxWidth}px` : column.maxWidth;
  }
  if (column.flex !== undefined) {
    styles.flex = `${column.flex} 1 0`;
  }

  return styles;
}

function getCellValue<T>(row: T, column: Column<T>): any {
  if (column.accessor) {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor];
  }
  return null;
}

function TableHeaderCell<T>({
  column,
  sortConfig,
  onSort,
  stickyHeader,
  stickyFirstColumn,
  isFirstColumn,
  density = "default",
  isLoading = false,
}: {
  column: Column<T>;
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  isFirstColumn?: boolean;
  density?: "compact" | "default" | "relaxed";
  isLoading?: boolean;
}) {
  const isSorted = sortConfig?.columnId === column.id;
  const sortDirection = isSorted ? sortConfig.direction : null;
  const isSticky = stickyFirstColumn && isFirstColumn;

  return (
    <div
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.sortable ? "gap-2" : "gap-1",
        densityClasses[density],
        alignmentClasses[column.align || "left"],
        stickyHeader && "sticky top-0 z-10",
        isSticky && "sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
        stickyHeader && isSticky && "z-30",
        column.sortable && !isLoading && "cursor-pointer hover:bg-background-hover",
        isLoading && "cursor-not-allowed opacity-60",
        isSorted ? "font-bold text-text-body" : "font-medium text-text-muted"
      )}
      onClick={() => !isLoading && column.sortable && onSort(column.id)}
      role="columnheader"
      aria-sort={isSorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
      aria-disabled={isLoading}
      style={getColumnStyles(column)}
    >
      {isLoading ? (
        <Skeleton className="h-4 w-24" />
      ) : (
        <>
          <span className="truncate">{column.label}</span>
          {column.sortable && (
            <div className="flex flex-shrink-0">
              {isSorted ? (
                <Icon
                  name={sortDirection === "asc" ? "CaretUp" : "CaretDown"}
                  size={14}
                  weight="bold"
                  className="text-text-body"
                />
              ) : (
                <Icon name="CaretUpDown" size={12} className="text-text-muted opacity-40" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TableCell<T>({
  column,
  row,
  rowIndex,
  columnIndex,
  isHovered,
  density = "default",
  className,
  stickyFirstColumn,
}: {
  column: Column<T>;
  row: T;
  rowIndex: number;
  columnIndex: number;
  isHovered: boolean;
  density?: "compact" | "default" | "relaxed";
  className?: string;
  stickyFirstColumn?: boolean;
}) {
  const isSticky = stickyFirstColumn && columnIndex === 0;
  const value = getCellValue(row, column);

  // Create the cell context
  const context: CellContext = {
    isLoading: false,
    isHovered,
    rowIndex,
    columnIndex,
    density,
  };

  let content;

  if (column.cell) {
    // Use the cell component
    const CellComponent = column.cell;
    const cellProps = typeof column.cellProps === "function" ? column.cellProps(value, row) : column.cellProps;

    content = <CellComponent value={value} row={row} context={context} {...cellProps} />;
  } else if (column.render) {
    // Use custom render function
    content = column.render(value, row, context);
  } else {
    // Default rendering
    content = <span className="truncate">{value?.toString() ?? ""}</span>;
  }

  // Wrap with popover if configured
  if (column.popover) {
    const popoverConfig = column.popover;

    // Parse popover configuration
    let popoverContent: React.ReactNode;
    let placement: "top" | "bottom" | "left" | "right" = "top";
    let showArrow = false;
    let trigger: "hover" | "click" = "hover";

    if (typeof popoverConfig === "string") {
      // Simple string tooltip
      popoverContent = popoverConfig;
    } else if (typeof popoverConfig === "function") {
      // Function that returns content
      popoverContent = popoverConfig(value, row);
    } else {
      // Object configuration
      popoverContent =
        typeof popoverConfig.content === "function" ? popoverConfig.content(value, row) : popoverConfig.content;
      placement = popoverConfig.placement || "top";
      showArrow = popoverConfig.showArrow ?? false;
      trigger = popoverConfig.trigger || "hover";
    }

    const triggerProps = trigger === "hover" ? { trigger: "longPress" as const } : {};

    content = (
      <DialogTrigger {...triggerProps}>
        <div className="cursor-help">{content}</div>
        <Popover placement={placement} showArrow={showArrow}>
          <div className="p-2">{popoverContent}</div>
        </Popover>
      </DialogTrigger>
    );
  }

  return (
    <div
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.noCellPadding ? "" : densityClasses[density],
        alignmentClasses[column.align || "left"],
        isSticky && "sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
        className
      )}
      role="cell"
      style={getColumnStyles(column)}
    >
      {content}
    </div>
  );
}

function LoadingCell({
  column,
  rowIndex,
  columnIndex,
  density = "default",
  stickyFirstColumn,
}: {
  column: Column<any>;
  rowIndex: number;
  columnIndex: number;
  density?: "compact" | "default" | "relaxed";
  stickyFirstColumn?: boolean;
}) {
  const isSticky = stickyFirstColumn && columnIndex === 0;
  const context: CellContext = {
    isLoading: true,
    rowIndex,
    columnIndex,
    density,
  };

  let content;

  if (column.cell) {
    // Use the cell component's loading state
    const CellComponent = column.cell;
    const cellProps = typeof column.cellProps === "function" ? column.cellProps(null, {} as any) : column.cellProps;

    content = <CellComponent value={null} row={{} as any} context={context} {...cellProps} />;
  } else if (column.render) {
    // Use custom render function with loading context
    content = column.render(null, {} as any, context);
  } else {
    // Default loading skeleton
    content = <Skeleton className="h-4 w-full max-w-[200px]" />;
  }

  return (
    <div
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.noCellPadding ? "" : densityClasses[density],
        alignmentClasses[column.align || "left"],
        isSticky && "sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
      )}
      role="cell"
      style={getColumnStyles(column)}
    >
      {content}
    </div>
  );
}

/**
 * Check if an element or any of its ancestors up to the boundary is an interactive element.
 * Interactive elements include buttons, links, inputs, selects, textareas, and elements
 * with interactive ARIA roles.
 */
function isInteractiveElement(target: HTMLElement | null, boundary: HTMLElement): boolean {
  const interactiveTags = new Set(["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA"]);
  const interactiveRoles = new Set(["button", "link", "checkbox", "switch", "menuitem", "option"]);

  let el = target;
  while (el && el !== boundary) {
    if (interactiveTags.has(el.tagName)) {
      return true;
    }

    const role = el.getAttribute("role");
    if (role && interactiveRoles.has(role)) {
      return true;
    }

    el = el.parentElement;
  }

  return false;
}

function TableRow<T>({
  columns,
  row,
  rowIndex,
  density = "default",
  onClick,
  stickyFirstColumn,
}: {
  columns: Column<T>[];
  row: T;
  rowIndex: number;
  density?: "compact" | "default" | "relaxed";
  onClick?: (row: T) => void;
  stickyFirstColumn?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;

      // Skip row click if the click originated from an interactive element
      // (e.g., button, link, input) to prevent both cell and row handlers from firing
      const target = event.target as HTMLElement | null;
      const currentTarget = event.currentTarget;

      if (isInteractiveElement(target, currentTarget)) {
        return;
      }

      onClick(row);
    },
    [onClick, row]
  );

  return (
    <div
      className={twMerge("contents group", onClick && "cursor-pointer")}
      role="row"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {columns.map((column, columnIndex) => (
        <TableCell
          key={column.id}
          column={column}
          row={row}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          isHovered={isHovered}
          density={density}
          className={onClick ? "group-hover:bg-background-hover" : ""}
          stickyFirstColumn={stickyFirstColumn}
        />
      ))}
    </div>
  );
}

function LoadingRow({
  columns,
  rowIndex,
  density = "default",
  stickyFirstColumn,
}: {
  columns: Column<any>[];
  rowIndex: number;
  density?: "compact" | "default" | "relaxed";
  stickyFirstColumn?: boolean;
}) {
  return (
    <div className="contents" role="row">
      {columns.map((column, columnIndex) => (
        <LoadingCell
          key={column.id}
          column={column}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          density={density}
          stickyFirstColumn={stickyFirstColumn}
        />
      ))}
    </div>
  );
}

/**
 * DataTable
 *
 * Advanced table component with sorting, filtering, pagination, and infinite scroll.
 * Supports custom cell renderers, column configurations, multiple display densities,
 * and virtualization for large datasets.
 */
export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  className,
  density = "default",
  width = "full",
  height,
  maxHeight,
  layout = "auto",
  mobileRenderer = "auto",
  customMobileRowRender,
  mobileBreakpoint = "md",
  isLoading = false,
  loadingState,
  loadingRowCount = 5,
  onLoadMore,
  hasMore = false,
  enableVirtualization,
  estimatedRowHeight,
  loadingIndicator,
  stickyHeader = false,
  stickyFirstColumn = false,
  onRowClick,
  getRowId,
  hideHeader = false,
  sortConfig: controlledSortConfig,
  onSort,
  "aria-label": ariaLabel,
  enableColumnReorder = false,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange,
}: DataTableProps<T>) {
  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig | null>(null);
  // Use controlled sortConfig if provided, otherwise use internal state
  const isControlled = controlledSortConfig !== undefined;
  const localSortConfig = isControlled ? controlledSortConfig : internalSortConfig;
  const tableRef = useRef<HTMLDivElement>(null);

  // Column order state - controlled or uncontrolled
  const defaultColumnOrder = useMemo(() => columns.map((col) => col.id), [columns]);
  const [internalColumnOrder, setInternalColumnOrder] = useState<string[]>(defaultColumnOrder);

  // Determine if column order is controlled
  const isColumnOrderControlled = controlledColumnOrder !== undefined;
  const localColumnOrder = isColumnOrderControlled ? controlledColumnOrder : internalColumnOrder;

  // Reconcile internalColumnOrder when columns prop changes (uncontrolled mode only)
  useEffect(() => {
    if (isColumnOrderControlled) return;
    setInternalColumnOrder((prev) => {
      const nextIds = columns.map((col) => col.id);
      const nextSet = new Set(nextIds);
      const prevSet = new Set(prev);
      // Keep existing columns that still exist, preserving their order
      const filtered = prev.filter((id) => nextSet.has(id));
      // Add any new columns at the end
      const missing = nextIds.filter((id) => !prevSet.has(id));
      return [...filtered, ...missing];
    });
  }, [columns, isColumnOrderControlled]);

  // Handle column order change
  const handleColumnOrderChange = useCallback(
    (newOrder: string[]) => {
      if (!isColumnOrderControlled) {
        setInternalColumnOrder(newOrder);
      }
      onColumnOrderChange?.(newOrder);
    },
    [isColumnOrderControlled, onColumnOrderChange]
  );

  // Get ordered columns based on columnOrder
  const orderedColumns = useMemo(() => {
    if (!enableColumnReorder && !controlledColumnOrder) {
      return columns;
    }
    const columnMap = new Map(columns.map((col) => [col.id, col]));
    return localColumnOrder.map((id) => columnMap.get(id)).filter((col): col is Column<T> => col !== undefined);
  }, [columns, localColumnOrder, enableColumnReorder, controlledColumnOrder]);

  // Setup infinite scroll if onLoadMore is provided
  const infiniteScroll = onLoadMore
    ? useInfiniteScroll({
        items: data,
        onLoadMore,
        hasMore,
        isLoading,
        loadingState,
        enableVirtualization,
        estimatedItemSize: estimatedRowHeight || (density === "compact" ? 40 : density === "relaxed" ? 60 : 48),
        overscan: 5,
      })
    : null;

  // Use infinite scroll refs if available
  const scrollRef = infiniteScroll?.scrollRef ?? tableRef;
  const { loadMoreRef, virtualizer, virtualItems, isLoadingMore, computedLoadingState } = infiniteScroll || {};

  const { gridTemplateColumns, totalMinWidth } = useMemo(() => {
    let minWidthSum = 0;

    const template = orderedColumns
      .map((col) => {
        // Track minWidth for calculating table minimum width
        if (col.minWidth) {
          minWidthSum += typeof col.minWidth === "number" ? col.minWidth : 0;
        } else if (col.width && typeof col.width === "number") {
          minWidthSum += col.width;
        }

        // Explicit width takes precedence
        if (col.width) {
          return typeof col.width === "number" ? `${col.width}px` : col.width;
        }
        // Flex-based sizing
        if (col.flex) {
          return `${col.flex}fr`;
        }
        // Min/max constraints
        if (col.minWidth && col.maxWidth) {
          const min = typeof col.minWidth === "number" ? `${col.minWidth}px` : col.minWidth;
          const max = typeof col.maxWidth === "number" ? `${col.maxWidth}px` : col.maxWidth;
          return `minmax(${min}, ${max})`;
        }
        if (col.minWidth) {
          const min = typeof col.minWidth === "number" ? `${col.minWidth}px` : col.minWidth;
          return `minmax(${min}, 1fr)`;
        }
        if (col.maxWidth) {
          const max = typeof col.maxWidth === "number" ? `${col.maxWidth}px` : col.maxWidth;
          return `minmax(0, ${max})`;
        }
        // Default to equal distribution
        return "1fr";
      })
      .join(" ");

    return { gridTemplateColumns: template, totalMinWidth: minWidthSum };
  }, [orderedColumns]);

  const handleSort = useCallback(
    (columnId: string) => {
      const column = columns.find((col) => col.id === columnId);
      if (!column?.sortable) return;

      let newSortConfig: SortConfig;

      if (localSortConfig?.columnId === columnId) {
        // Toggle direction when clicking same column
        newSortConfig = {
          columnId,
          direction: localSortConfig.direction === "asc" ? "desc" : "asc",
        };
      } else {
        // New column, start with asc
        newSortConfig = { columnId, direction: "asc" };
      }

      // Only update internal state if not controlled
      if (!isControlled) {
        setInternalSortConfig(newSortConfig);
      }
      onSort?.(newSortConfig);
    },
    [columns, localSortConfig, onSort, isControlled]
  );

  // Determine if we're in initial loading state
  const isInitialLoad = computedLoadingState === "loading" || (isLoading && data.length === 0);

  // Container styles for height control
  const containerStyle: React.CSSProperties = {};
  if (height) {
    containerStyle.height = typeof height === "number" ? `${height}px` : height;
    containerStyle.overflowY = "auto";
  } else if (maxHeight) {
    containerStyle.maxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
    containerStyle.overflowY = "auto";
  } else if (onLoadMore) {
    // Infinite scroll requires explicit height for scrolling
    containerStyle.height = "600px";
    containerStyle.overflowY = "auto";
  }

  // Determine wrapper classes based on layout
  // When infinite scroll is enabled, we manage overflow via inline styles
  // Add scrollbar-visible to ensure scrollbars are always visible for accessibility
  const wrapperClasses = twMerge(
    widthClasses[width],
    !onLoadMore && (layout === "responsive" ? "overflow-x-auto scrollbar-visible" : "overflow-auto scrollbar-visible"),
    className
  );

  // Get breakpoint classes for mobile/desktop visibility
  const breakpointClasses = getMobileBreakpointClasses(mobileBreakpoint);
  const showMobile = mobileRenderer !== "none";

  // Determine which mobile renderer to use
  const renderMobileRow = (row: T, index: number) => {
    if (!showMobile) return null;

    if (mobileRenderer === "custom" && customMobileRowRender) {
      return customMobileRowRender(row, index);
    }

    const MobileComponent = mobileRenderer === "cards" ? CardMobileRenderer : AutoMobileRenderer;

    return (
      <MobileComponent
        row={row}
        columns={orderedColumns}
        density={density}
        isLoading={isLoading}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
      />
    );
  };

  // Determine which rows to render (virtualized or all)
  const rowsToRender = virtualizer
    ? virtualItems?.map((virtualItem) => ({
        virtualItem,
        row: data[virtualItem.index],
        index: virtualItem.index,
      })) || []
    : data.map((row, index) => ({ row, index, virtualItem: null }));

  return (
    <div
      ref={scrollRef}
      className={wrapperClasses}
      style={{
        ...containerStyle,
        position: virtualizer ? "relative" : undefined,
      }}
    >
      {/* Desktop View */}
      <div
        className={twMerge(
          "grid border-separate border-spacing-0",
          layoutClasses[layout],
          width === "auto" ? "inline-grid w-auto" : "w-full",
          showMobile && breakpointClasses.desktop
        )}
        role="table"
        aria-label={ariaLabel}
        style={{
          gridTemplateColumns,
          // Set minimum width based on sum of column minWidths to enable horizontal scrolling
          minWidth: totalMinWidth > 0 ? `${totalMinWidth}px` : undefined,
        }}
      >
        {/* Header */}
        {!hideHeader &&
          (enableColumnReorder ? (
            <SortableHeaderRow
              columns={columns}
              columnOrder={localColumnOrder}
              onColumnOrderChange={handleColumnOrderChange}
              sortConfig={localSortConfig}
              onSort={handleSort}
              stickyHeader={stickyHeader}
              stickyFirstColumn={stickyFirstColumn}
              density={density}
              isLoading={isInitialLoad}
            />
          ) : (
            <div className="contents" role="rowgroup">
              {orderedColumns.map((column, index) => (
                <TableHeaderCell
                  key={column.id}
                  column={column}
                  sortConfig={localSortConfig}
                  onSort={handleSort}
                  stickyHeader={stickyHeader}
                  stickyFirstColumn={stickyFirstColumn}
                  isFirstColumn={index === 0}
                  density={density}
                  isLoading={isInitialLoad}
                />
              ))}
            </div>
          ))}

        {/* Body */}
        {virtualizer ? (
          // Virtualized body - wrapping div with virtual height
          <div
            className="col-span-full"
            style={{
              gridColumn: `1 / -1`,
              height: `${virtualizer.getTotalSize()}px`,
              position: "relative",
            }}
            role="rowgroup"
          >
            {isInitialLoad ? (
              // Initial loading state - show skeletons
              Array.from({ length: loadingRowCount }).map((_, index) => (
                <div key={`loading-${index}`} className="contents">
                  <LoadingRow
                    columns={orderedColumns}
                    rowIndex={index}
                    density={density}
                    stickyFirstColumn={stickyFirstColumn}
                  />
                </div>
              ))
            ) : data.length > 0 ? (
              // Virtualized data rows
              <>
                {rowsToRender.map(({ row, index, virtualItem }) => {
                  if (!row || !virtualItem) return null;
                  const rowId = getRowId ? getRowId(row) : `row-${index}`;

                  return (
                    <div
                      key={rowId}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                    >
                      <TableRow
                        columns={orderedColumns}
                        row={row}
                        rowIndex={index}
                        density={density}
                        onClick={onRowClick}
                        stickyFirstColumn={stickyFirstColumn}
                      />
                    </div>
                  );
                })}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div
                    style={{
                      position: "absolute",
                      top: `${virtualizer.getTotalSize()}px`,
                      left: 0,
                      width: "100%",
                    }}
                  >
                    {loadingIndicator || <InfiniteScrollIndicator state="loading-more" />}
                  </div>
                )}
              </>
            ) : (
              // Empty state
              <div className="py-12 text-center text-text-muted">No data available</div>
            )}
          </div>
        ) : (
          // Non-virtualized body
          <div className="contents" role="rowgroup">
            {isInitialLoad ? (
              // Initial loading state - show skeletons
              Array.from({ length: loadingRowCount }).map((_, index) => (
                <LoadingRow
                  key={`loading-${index}`}
                  columns={orderedColumns}
                  rowIndex={index}
                  density={density}
                  stickyFirstColumn={stickyFirstColumn}
                />
              ))
            ) : data.length > 0 ? (
              // Data rows
              <>
                {rowsToRender.map(({ row, index }) => {
                  if (!row) return null;
                  const rowId = getRowId ? getRowId(row) : `row-${index}`;

                  return (
                    <TableRow
                      key={rowId}
                      columns={orderedColumns}
                      row={row}
                      rowIndex={index}
                      density={density}
                      onClick={onRowClick}
                      stickyFirstColumn={stickyFirstColumn}
                    />
                  );
                })}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="col-span-full" style={{ gridColumn: `1 / -1` }}>
                    {loadingIndicator || <InfiniteScrollIndicator state="loading-more" />}
                  </div>
                )}
              </>
            ) : (
              // Empty state
              <div className="col-span-full py-12 text-center text-text-muted" role="row">
                <div role="cell">No data available</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile View */}
      {showMobile && (
        <div className={breakpointClasses.mobile}>
          {isInitialLoad ? (
            // Mobile initial loading state
            Array.from({ length: loadingRowCount }).map((_, index) => (
              <MobileLoadingSkeleton key={`mobile-loading-${index}`} density={density} />
            ))
          ) : data.length > 0 ? (
            // Mobile data rows
            <>
              {rowsToRender.map(({ row, index }) => {
                if (!row) return null;
                const rowId = getRowId ? getRowId(row) : `mobile-row-${index}`;
                return (
                  <div key={rowId} className="border-b border-border-default last:border-b-0">
                    {renderMobileRow(row, index)}
                  </div>
                );
              })}

              {/* Mobile loading more indicator */}
              {isLoadingMore && (loadingIndicator || <InfiniteScrollIndicator state="loading-more" />)}
            </>
          ) : (
            // Mobile empty state
            <div className="py-12 text-center text-text-muted">No data available</div>
          )}
        </div>
      )}

      {/* Intersection observer target - positioned based on virtualization */}
      {onLoadMore &&
        hasMore &&
        !isInitialLoad &&
        (virtualizer ? (
          // For virtualized: position at the end of virtual content
          <div
            ref={loadMoreRef}
            style={{
              position: "absolute",
              top: `${virtualizer.getTotalSize()}px`,
              left: 0,
              width: "100%",
              height: 1,
            }}
          />
        ) : (
          // For non-virtualized: simple element at the end
          <div ref={loadMoreRef} className="h-px w-full" />
        ))}
    </div>
  );
}
